<template>
	<div>
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<Col
			v-click-outside="{ handler: () => (isSelected = false) }"
			:class="{ selected: isSelected }"
			@click.native="isSelected = true"
			class="width-100 box-container mb-3 flex-centerize"
		>
			<input
				class="file-upload-input"
				id="file_upload_input"
				type="file"
				data-lpignore="true"
				:multiple="isMultipleFiles"
				:name="'this_is_my_name'"
				:disabled="loading"
				@change="onChangeFile"
				:key="reRenderFileUpload"
				:class="{ 'pointer-events-none': !isSelected }"
				ref="file_input"
			/>
			<FileInputSvg class="mb-1" />
			<h3 class="reg mb-1">
				{{ isMultipleFiles ? 'Drop or paste files here' : 'Drop or paste file here' }}
			</h3>
			<AnemicButton @click="browseFiles" tile class="px-0 browse-button"> Browse files </AnemicButton>
		</Col>
		<div class="file-input-field px-2">
			<div class="files-progress-section">
				<div
					v-for="fileName in fileNames"
					:key="fileName"
					class="file-progress-wrapper mb-2"
					:class="{ 'is-finished': filesDataMap[fileName].progressStatus === 100 }"
				>
					<div class="flex-space-between--center px-1">
						<Row class="flex-align-center">
							<a :href="filesDataMap[fileName].fileUrl" v-if="filesDataMap[fileName].fileUrl" class="mr-2"> Download file </a>
							<h3 class="file-name reg text-overflow-ellipsis">{{ fileName }}</h3>
						</Row>
						<div @click="remove(fileName)">
							<AppTooltip
								noArrow
								:disabled="filesDataMap[fileName]?.progressStatus === 100"
								text="Uploading, after upload is done, you'll be able to remove"
							>
								<CloseButton :disabled="filesDataMap[fileName]?.progressStatus !== 100" hasCircle class="remove-file" />
							</AppTooltip>
						</div>
					</div>

					<v-progress-linear v-model="filesDataMap[fileName].progressStatus" data-lpignore="true" height="2" indeterminate>
						<strong>{{ Math.ceil(filesDataMap[fileName].progressStatus) }}%</strong>
					</v-progress-linear>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { uploadWidgetFile } from '@/utils/helpers';
import WidgetFieldMixin from './WidgetFieldMixin';
import DefaultWidgetField from './DefaultWidgetField';
import FileInputSvg from './assets/FileInputSvg';

export default {
	name: 'FileInputField',
	mixins: [WidgetFieldMixin],
	components: {
		DefaultWidgetField,
		FileInputSvg,
	},
	data() {
		return {
			reRenderFileUpload: 0,
			filesDataMap: {},
			loading: false,
			uploadProgress: 0,
			outlinedInput: false, //Customization for WidgetFieldMixin default behavior
			isSelected: false,
			onPasteHandler: null,
		};
	},
	watch: {
		isSelected: {
			handler(newValue) {
				if (newValue) {
					this.listenOnPaste();
				} else {
					this.removeOnPasteListener();
				}
			},
			immediate: true,
		},
	},
	computed: {
		widgetID() {
			return this.field?.widget;
		},
		fileNames() {
			return Object.keys(this.filesDataMap);
		},
		isMultipleFiles() {
			return this.fieldOptions.user_selection.multiple;
		},
		selectedFiles() {
			return this.fileNames.filter((file) => this.filesDataMap[file].progressStatus === 100);
		},
	},
	beforeDestroy() {
		this.removeOnPasteListener();
	},
	methods: {
		browseFiles() {
			this.$refs.file_input.click();
		},
		onPaste(event) {
			if (this.isSelected) {
				event.preventDefault();
				if (!event.clipboardData.files.length) {
					return;
				}
				// Iterate over all pasted files.
				const filesToUpload = [];
				const allUploadedFiles = Object.values(this.filesDataMap).map(({ fileObject }) => fileObject);
				Array.from(event.clipboardData.files).forEach(async (blob) => {
					let fileName = blob.name;
					const isThereUploadedFileWithTheSameName = allUploadedFiles.some((uploadedFile) => uploadedFile.name === fileName);
					if (isThereUploadedFileWithTheSameName) {
						// Otherwise, it will be overwritten.
						fileName = `${fileName}-${blob.lastModified}`;
					}
					const file = new File([blob], fileName, { type: blob.type });
					filesToUpload.push(file);
				});
				const allFiles = [...filesToUpload, ...allUploadedFiles];
				const filesToUploadConsideringMultiple = this.isMultipleFiles ? allFiles : allFiles.slice(0, 1);
				this.filesChange(filesToUploadConsideringMultiple);
			}
		},
		removeOnPasteListener() {
			if (this.onPasteHandler) {
				window.removeEventListener('paste', this.onPasteHandler, true);
				this.onPasteHandler = null;
			}
		},
		listenOnPaste() {
			this.removeOnPasteListener();
			this.onPasteHandler = (e) => this.onPaste(e);
			window.addEventListener('paste', this.onPasteHandler, true);
		},
		updateUploadingState(newState) {
			this.$emit('field-event', {
				value: newState,
				action: 'isUploadingFile',
			});
		},
		onChangeFile(event) {
			this.filesChange(event.target.files);
			this.fileCount = event.target.files.length;
		},
		async _uploadFile(fileData) {
			const file = fileData.fileObject;
			try {
				this.setIsUploading(true);
				const uploadedFile = await this.uploadFile({
					file,
					widgetId: this.widgetID,
					onUploadProgress: (e) => this.onUploadProgress(e, fileData),
				});
				this.filesDataMap[file.name].fileUrl = uploadedFile;
				if (this.isMultipleFiles) {
					this.setMultipleFilesFieldValue();
					if (this.selectedFiles.length === this.fileNames.length) {
						this.setIsUploading(false);
					}
				} else {
					this.setFieldValue(uploadedFile, file.name);
					this.setIsUploading(false);
				}
			} catch (err) {
				console.error('file upload error', err);
				this.setIsUploading(false);
				alert('Error while uploading file');
			}
		},
		uploadFile({ file, widgetId, onUploadProgress }) {
			return uploadWidgetFile({ file, widgetId, onUploadProgress });
		},
		onUploadProgress(progressEvent, fileData) {
			fileData.progressStatus = Math.round((progressEvent.loaded * 100) / progressEvent.total);
		},
		filesChange(fileList) {
			if (!this.isMultipleFiles) {
				this.singleFileDelete();
			}
			// handle file changes
			for (let i = 0; i < fileList.length; i++) {
				const file = fileList[i];
				this.$set(this.filesDataMap, file.name, { fileObject: file, progressStatus: 0 });
			}

			this.setIsUploading(true);

			this.fileNames.forEach((fileName) => {
				this._uploadFile(this.filesDataMap[fileName]);
			});
		},
		remove(fileName) {
			if (this.isMultipleFiles) {
				this.$delete(this.filesDataMap, fileName);
				this.setMultipleFilesFieldValue();
			} else {
				this.singleFileDelete();
			}
			this.reRenderFileUpload++;
		},
		setMultipleFilesFieldValue() {
			const fileNames = this.selectedFiles;
			const fileUrls = fileNames.map((fName) => this.filesDataMap[fName].fileUrl);
			this.setFieldValue(fileUrls, fileNames);
		},
		setIsUploading(isUploading) {
			this.loading = isUploading;
			this.updateUploadingState(isUploading);
		},
		singleFileDelete() {
			this.setFieldValue(null);
			this.$set(this, 'filesDataMap', {});
		},
	},
};
</script>

<style scoped lang="scss">
.file-input-field {
	margin-left: -8px;

	.custom-file-upload {
		position: relative;
		// display: block;
		height: 100px;
		width: 100%;
		// background: yellow;
		border: 1px dashed #cbcbcb;
		border-radius: 7px;
		transition: 0.2s;

		&:hover {
			filter: brightness(110%);
			cursor: pointer;
			background-color: var(--hover-bg-color);
			transition: 0.3s;
		}

		&::-webkit-file-upload-button {
			display: none;
		}
		.link-like {
			color: #5f8ff1;
		}

		input[type='file'] {
			position: absolute;
			top: 0;
			right: 0;
			min-width: 100%;
			min-height: 100%;
			// font-size: 100px;
			text-align: right;
			filter: alpha(opacity=0);
			opacity: 0;
			outline: none;
			background: transparent; //white;
			cursor: inherit;
			display: block;
		}
	}

	.files-progress-section {
		max-height: 120px;
		overflow: auto;
		.file-progress-wrapper {
			::v-deep(.v-progress-linear) {
				background-color: #eef1f6;

				.v-progress-linear__indeterminate .primary {
					background-color: #5f8ff1 !important;
					border-color: #5f8ff1 !important;
				}
				.v-progress-linear__content {
					display: none;
				}
			}
		}
		.is-finished {
			::v-deep(.v-progress-linear) {
				background-color: #5f8ff1;
			}
		}
	}
}

.box-container {
	cursor: pointer;
	min-height: 111px;
	border-radius: 7px;
	border: 1px dashed #cbcbcb;
	transition: var(--transition-time);

	&:hover {
		background: #f7f9fc;
		transition: var(--transition-time);
		border: 1px dashed #5f8ff2;
	}
	&.selected {
		background: #f7f9fc;
		border: 1px solid #5f8ff2;
		transition: var(--transition-time);
	}
}
.browse-button {
	height: 25px !important;
	width: 100px !important;
	font-size: 14px !important;
	color: #ffffff !important;
	border-radius: 7px !important;
	letter-spacing: 0px !important;
	text-transform: none !important;
	background: #5f8ff2 !important;
	border: 1px solid #5f8ff2 !important;
}
.file-upload-input {
	opacity: 0;
	position: absolute;
	cursor: pointer !important;
	width: -webkit-fill-available;
	height: -webkit-fill-available;
	max-height: 111px;
	width: 100% !important;
	max-width: calc(100% - 80px) !important;
}
</style>
